/**
 * Analytics Utilities
 *
 * Google Analytics 4 integration and event tracking.
 * Also supports Core Web Vitals reporting.
 */

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// Get GA Measurement ID from environment or use placeholder
const GA_MEASUREMENT_ID = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_GA_MEASUREMENT_ID || '';

/**
 * Initialize Google Analytics
 */
export function initGA(): void {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') {
    return;
  }

  // Don't initialize twice
  if (typeof window.gtag === 'function') {
    return;
  }

  // Create script element
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll send manually for SPA
  });
}

/**
 * Track page view
 */
export function trackPageView(path: string, title?: string): void {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

/**
 * Track custom event
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  window.gtag('event', eventName, params);
}

/**
 * Track form submission
 */
export function trackFormSubmission(formId: string, formType: string): void {
  trackEvent('form_submission', {
    form_id: formId,
    form_type: formType,
  });
}

/**
 * Track form view
 */
export function trackFormView(formId: string, formType: string): void {
  trackEvent('form_view', {
    form_id: formId,
    form_type: formType,
  });
}

/**
 * Track quiz participation
 */
export function trackQuizJoin(quizId: string, gamePin: string): void {
  trackEvent('quiz_join', {
    quiz_id: quizId,
    game_pin: gamePin,
  });
}

/**
 * Track user registration
 */
export function trackSignUp(method: string = 'email'): void {
  trackEvent('sign_up', {
    method,
  });
}

/**
 * Track user login
 */
export function trackLogin(method: string = 'email'): void {
  trackEvent('login', {
    method,
  });
}

/**
 * Track form creation
 */
export function trackFormCreation(formType: string): void {
  trackEvent('form_created', {
    form_type: formType,
  });
}

/**
 * Track form publication
 */
export function trackFormPublish(formId: string, formType: string): void {
  trackEvent('form_published', {
    form_id: formId,
    form_type: formType,
  });
}

/**
 * Track language change
 */
export function trackLanguageChange(language: string): void {
  trackEvent('language_change', {
    language,
  });
}

/**
 * Report Core Web Vitals
 * Note: Requires 'web-vitals' package to be installed
 */
export function reportWebVitals(): void {
  // Web vitals reporting is optional - only works if package is installed
  // To enable: npm install web-vitals
  // Then uncomment the code below
  /*
  if (typeof window === 'undefined') return;
  import('web-vitals').then(({ onCLS, onFID, onFCP, onLCP, onTTFB }) => {
    const sendToAnalytics = ({ name, value, id }: { name: string; value: number; id: string }) => {
      trackEvent('web_vitals', {
        metric_name: name,
        metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
        metric_id: id,
      });
    };
    onCLS(sendToAnalytics);
    onFID(sendToAnalytics);
    onFCP(sendToAnalytics);
    onLCP(sendToAnalytics);
    onTTFB(sendToAnalytics);
  }).catch(() => {});
  */
}

/**
 * React hook for page view tracking
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
}
